import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * Variant picker
 */
export default class VariantPicker extends Component {
  #pendingRequestUrl;
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();

    const fieldsets =
      /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    this.#checkedIndices = [];
    this.#radios = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);

      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex(
        (radio) =>
          radio.checked ||
          radio.dataset.currentChecked === 'true'
      );

      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));

    this.#resizeObserver.observe(this);

    this.updateAllDisplayedOptionValues();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles variant selection.
   *
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (!selectedOption) return;

    /*
     * Update the checked/selected state BEFORE Shopify
     * builds the section request.
     */
    this.updateSelectedOption(event.target);

    /*
     * IMPORTANT:
     * Change the visible "Color: color name" immediately.
     */
    this.updateDisplayedOptionValue(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;

    const loadsNewProduct =
      isOnProductPage &&
      !!newUrl &&
      newUrl !== currentUrl;

    const isOnFeaturedProductSection = Boolean(
      this.closest('featured-product-information')
    );

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
        ? 'featured-product-information'
        : undefined;

    const {
      optionValueId = '',
      variantId = '',
      connectedProductUrl = '',
    } = selectedOption.dataset;

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption),
      {
        morphElementSelector,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
      }
    );

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Immediately updates the displayed value next to the option name.
   *
   * Example:
   * Color: Pink
   * Color: Chartreuse
   *
   * @param {Element} target
   */
  updateDisplayedOptionValue(target) {
    if (target instanceof HTMLInputElement) {
      const fieldset = target.closest('fieldset');

      if (!fieldset) return;

      const valueElement = fieldset.querySelector(
        '.variant-option__swatch-value'
      );

      if (valueElement) {
        valueElement.textContent = target.value;
      }

      return;
    }

    if (target instanceof HTMLSelectElement) {
      const wrapper = target.closest('.variant-option');

      if (!wrapper) return;

      const valueElement = wrapper.querySelector(
        '.variant-option__swatch-value'
      );

      if (valueElement) {
        valueElement.textContent = target.value;
      }
    }
  }

  /**
   * Makes sure all option headings display the currently
   * checked value when the component first loads.
   */
  updateAllDisplayedOptionValues() {
    const fieldsets =
      /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    for (const fieldset of fieldsets) {
      const checked = fieldset.querySelector('input:checked');

      if (!(checked instanceof HTMLInputElement)) continue;

      const valueElement = fieldset.querySelector(
        '.variant-option__swatch-value'
      );

      if (valueElement) {
        valueElement.textContent = checked.value;
      }
    }

    for (const select of this.querySelectorAll('select')) {
      if (!(select instanceof HTMLSelectElement)) continue;

      this.updateDisplayedOptionValue(select);
    }
  }

  /**
   * Gets fieldset measurements.
   *
   * @param {number} fieldsetIndex
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    /*
     * FIX:
     * Previous version had:
     *
     * const s = ...
     *
     * and then tried to use:
     *
     * fieldsets[fieldsetIndex]
     *
     * That caused fieldsets to be undefined.
     */
    const fieldsets =
      /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) {
      return null;
    }

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,

      currentWidth:
        currentIndex !== undefined
          ? radios[currentIndex]?.parentElement?.offsetWidth
          : undefined,

      previousWidth:
        previousIndex !== undefined
          ? radios[previousIndex]?.parentElement?.offsetWidth
          : undefined,
    };
  }

  /**
   * Applies measurements to fieldset.
   */
  #applyFieldsetMeasurements({
    fieldset,
    currentWidth,
    previousWidth,
    currentIndex,
    previousIndex,
  }) {
    if (currentWidth) {
      fieldset.style.setProperty(
        '--pill-width-current',
        `${currentWidth}px`
      );
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty(
        '--pill-width-previous',
        `${previousWidth}px`
      );
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates fieldset CSS.
   *
   * @param {number} fieldsetIndex
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements =
      this.#getFieldsetMeasurements(fieldsetIndex);

    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates selected radio/select.
   *
   * @param {string | Element} target
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]`
      );

      if (!targetElement) {
        throw new Error('Target element not found');
      }

      target = targetElement;
    }

    /*
     * RADIO / SWATCH
     */
    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(
        target.dataset.fieldsetIndex || ''
      );

      const inputIndex = Number.parseInt(
        target.dataset.inputIndex || ''
      );

      if (
        !Number.isNaN(fieldsetIndex) &&
        !Number.isNaN(inputIndex)
      ) {
        const fieldsets =
          /** @type {HTMLFieldSetElement[]} */ (
            this.refs.fieldsets || []
          );

        const fieldset = fieldsets[fieldsetIndex];

        const checkedIndices =
          this.#checkedIndices[fieldsetIndex];

        const radios =
          this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] =
            checkedIndices;

          /*
           * Clear old states.
           */
          if (
            currentIndex !== undefined &&
            radios[currentIndex]
          ) {
            radios[currentIndex].dataset.previousChecked =
              'false';

            radios[currentIndex].dataset.currentChecked =
              'false';
          }

          if (
            previousIndex !== undefined &&
            radios[previousIndex]
          ) {
            radios[previousIndex].dataset.previousChecked =
              'false';

            radios[previousIndex].dataset.currentChecked =
              'false';
          }

          /*
           * Store new current and previous choice.
           */
          checkedIndices.unshift(inputIndex);

          checkedIndices.length = Math.min(
            checkedIndices.length,
            2
          );

          const newCurrentIndex =
            checkedIndices[0];

          const newPreviousIndex =
            checkedIndices[1];

          if (
            newCurrentIndex !== undefined &&
            radios[newCurrentIndex]
          ) {
            radios[
              newCurrentIndex
            ].dataset.currentChecked = 'true';

            radios[
              newCurrentIndex
            ].dataset.previousChecked = 'false';
          }

          if (
            newPreviousIndex !== undefined &&
            radios[newPreviousIndex]
          ) {
            radios[
              newPreviousIndex
            ].dataset.previousChecked = 'true';

            radios[
              newPreviousIndex
            ].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }

      target.checked = true;
    }

    /*
     * DROPDOWN
     */
    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;

      const newSelectedOption = Array.from(
        target.options
      ).find(
        (option) => option.value === newValue
      );

      if (!newSelectedOption) {
        throw new Error('Option not found');
      }

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute(
        'selected',
        'selected'
      );
    }
  }

  /**
   * Creates Shopify section request URL.
   *
   * @param {HTMLElement} selectedOption
   * @param {string | null} source
   * @param {string[]} sourceSelectedOptionsValues
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;

    this.#pendingRequestUrl = productUrl;

    const params = [];

    const viewParamValue =
      getViewParameterValue();

    if (viewParamValue) {
      params.push(`view=${viewParamValue}`);
    }

    /*
     * IMPORTANT:
     * Send ALL current selected option IDs to Shopify.
     */
    if (
      this.selectedOptionsValues.length &&
      !source
    ) {
      params.push(
        `option_values=${this.selectedOptionsValues.join(',')}`
      );
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(
          `option_values=${sourceSelectedOptionsValues.join(',')}`
        );
      } else {
        params.push(
          `option_values=${selectedOption.dataset.optionValueId}`
        );
      }
    }

    const SECTION_ID_MAP = {
      'quick-add-component':
        'section-rendering-product-card',

      'swatches-variant-picker-component':
        'section-rendering-product-card',

      'featured-product-information':
        this.closest(
          'featured-product-information'
        )?.id,
    };

    const closestSectionId =
      /** @type {keyof typeof SECTION_ID_MAP | undefined} */ (
        Object.keys(SECTION_ID_MAP).find(
          (sectionId) => this.closest(sectionId)
        )
      );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }

      return `${productUrl}?section_id=${
        SECTION_ID_MAP[closestSectionId]
      }&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches updated Shopify product/variant data.
   */
  fetchUpdatedSection(
    requestUrl,
    {
      morphElementSelector,
      detail = {},
    } = {}
  ) {
    const {
      optionValueId = '',
      variantId,
      connectedProductUrl = '',
    } = detail;

    /*
     * Cancel old request if user clicks
     * colors quickly.
     */
    this.#abortController?.abort();

    this.#abortController =
      new AbortController();

    const deferredEventPromise =
      ProductSelectEvent.createPromise();

    const selectedOptions =
      this.getAllSelectedOptions();

    /*
     * Fire Shopify product-select event.
     */
    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },

        selectedOptions,

        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },

        promise:
          deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, {
      signal:
        this.#abortController.signal,
    })
      .then((response) => response.text())

      .then((responseText) => {
        this.#pendingRequestUrl = undefined;

        const html =
          new DOMParser().parseFromString(
            responseText,
            'text/html'
          );

        html
          .querySelector(
            'overflow-list[defer]'
          )
          ?.removeAttribute('defer');

        const variantPickerJsonScript =
          html.querySelector(
            'variant-picker script[type="application/json"]'
          );

        const textContent =
          variantPickerJsonScript?.textContent;

        /*
         * No variant returned.
         */
        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,

            detail: {
              html,

              productId:
                this.dataset.productId ?? '',

              sourceId:
                this.selectedOptionId,

              resource: null,
            },
          });

          return;
        }

        let newProduct;

        /*
         * Update page content.
         */
        if (
          morphElementSelector === 'main'
        ) {
          this.updateMain(html);

        } else if (morphElementSelector) {
          this.updateElement(
            html,
            morphElementSelector
          );

        } else {
          const { overflowList } =
            this.refs;

          const wasSwatchesExpanded =
            overflowList instanceof OverflowList &&
            overflowList.getAttribute('disabled') ===
              'true';

          newProduct =
            this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph =
              overflowList;

            if (
              overflowListAfterMorph instanceof
              OverflowList
            ) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        /*
         * Reapply visible option names after Shopify morph.
         */
        this.updateAllDisplayedOptionValues();

        /*
         * Send updated variant information to
         * Shopify's product form / add-to-cart code.
         */
        if (this.selectedOptionId) {
          const variantData =
            JSON.parse(textContent);

          if (
            variantData &&
            typeof variantData === 'object'
          ) {
            const productViewAttr =
              variantPickerJsonScript
                ?.closest(
                  '[view-event-payload]'
                )
                ?.getAttribute(
                  'view-event-payload'
                )
                ?.trim();

            deferredEventPromise.resolve({
              variant:
                (
                  productViewAttr &&
                  JSON.parse(
                    productViewAttr
                  )
                )?.product?.selectedVariant ??
                null,

              detail: {
                html,

                productId:
                  this.dataset.productId ??
                  '',

                newProduct,

                sourceId:
                  this.selectedOptionId,

                resource:
                  variantData,
              },
            });

            return;
          }
        }

        deferredEventPromise.resolve({
          variant: null,

          detail: {
            html,

            productId:
              this.dataset.productId ?? '',

            newProduct,

            sourceId:
              this.selectedOptionId,

            resource: null,
          },
        });
      })

      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }

        deferredEventPromise.reject(error);

        console.error(error);
      });
  }

  /**
   * Re-renders variant picker.
   *
   * @param {Document | Element} newHtml
   */
  updateVariantPicker(newHtml) {
    let newProduct;

    const newVariantPickerSource =
      newHtml.querySelector(
        this.tagName.toLowerCase()
      );

    if (!newVariantPickerSource) {
      throw new Error(
        'No new variant picker source found'
      );
    }

    if (
      newVariantPickerSource instanceof HTMLElement
    ) {
      const newProductId =
        newVariantPickerSource.dataset.productId;

      const newProductUrl =
        newVariantPickerSource.dataset.productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !==
          newProductId
      ) {
        newProduct = {
          id: newProductId,
          url: newProductUrl,
        };
      }

      this.dataset.productId =
        newProductId;

      this.dataset.productUrl =
        newProductUrl;
    }

    morph(
      this,
      newVariantPickerSource,
      {
        ...MORPH_OPTIONS,

        getNodeKey: (node) => {
          if (
            !(node instanceof HTMLElement)
          ) {
            return undefined;
          }

          return node.dataset.key;
        },
      }
    );

    /*
     * Rebuild radio references after Shopify
     * replaces picker HTML.
     */
    const fieldsets =
      /** @type {HTMLFieldSetElement[]} */ (
        this.refs.fieldsets || []
      );

    this.#radios = [];
    this.#checkedIndices = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(
        fieldset.querySelectorAll('input')
      );

      this.#radios.push(radios);

      const checkedIndex =
        radios.findIndex(
          (radio) => radio.checked
        );

      if (checkedIndex >= 0) {
        this.#checkedIndices.push([
          checkedIndex,
        ]);
      } else {
        this.#checkedIndices.push([]);
      }
    });

    this.updateVariantPickerCss();

    this.updateAllDisplayedOptionValues();

    return newProduct;
  }

  /**
   * Updates animated picker pill widths.
   */
  updateVariantPickerCss() {
    const fieldsets =
      /** @type {HTMLFieldSetElement[]} */ (
        this.refs.fieldsets || []
      );

    const measurements = fieldsets
      .map((_, index) =>
        this.#getFieldsetMeasurements(index)
      )
      .filter(
        (measurement) =>
          measurement !== null
      );

    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(
        measurement
      );
    }
  }

  /**
   * Re-renders section.
   */
  updateElement(
    newHtml,
    elementSelector
  ) {
    const element =
      this.closest(elementSelector);

    const newElement =
      newHtml.querySelector(
        elementSelector
      );

    if (!element || !newElement) {
      throw new Error(
        `No new element source found for ${elementSelector}`
      );
    }

    morph(element, newElement);
  }

  /**
   * Re-renders main page.
   */
  updateMain(newHtml) {
    const main =
      document.querySelector('main');

    const newMain =
      newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error(
        'No new main source found'
      );
    }

    morph(main, newMain);
  }

  /**
   * Currently selected option.
   */
  get selectedOption() {
    /*
     * Prefer radio currently checked.
     */
    const checkedInput =
      this.querySelector(
        'fieldset input:checked'
      );

    if (
      checkedInput instanceof
      HTMLInputElement
    ) {
      return checkedInput;
    }

    /*
     * Otherwise return current dropdown option.
     */
    const select =
      this.querySelector('select');

    if (
      select instanceof HTMLSelectElement
    ) {
      const option =
        select.options[
          select.selectedIndex
        ];

      if (
        option instanceof
        HTMLOptionElement
      ) {
        return option;
      }
    }

    return undefined;
  }

  /**
   * Returns selected option name/value pairs.
   */
  getAllSelectedOptions() {
    const options = [];

    /*
     * Champy's V134: collect selections in the same DOM/Shopify option order
     * they are rendered. The previous code always put dropdowns before swatches,
     * which reversed Color + Tail on the 2-inch Crusher when Color was a swatch
     * and Tail was a dropdown. That could resolve a non-existent/wrong variant.
     */
    for (const control of this.querySelectorAll('select, fieldset input:checked')) {
      let selected = control;

      if (control instanceof HTMLSelectElement) {
        selected = control.options[control.selectedIndex];
      }

      if (!(selected instanceof HTMLElement)) continue;

      const name = selected.dataset?.optionName;
      const value = selected instanceof HTMLInputElement || selected instanceof HTMLOptionElement
        ? selected.value
        : '';

      if (name) options.push({ name, value });
    }

    return options;
  }

  /**
   * Current option-value ID.
   */
  get selectedOptionId() {
    const {
      selectedOption,
    } = this;

    if (!selectedOption) {
      return undefined;
    }

    const {
      optionValueId,
    } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error(
        'No option value ID found'
      );
    }

    return optionValueId;
  }

  /**
   * ALL currently selected Shopify
   * option-value IDs.
   *
   * This is important when adding different
   * colors/variants to the cart.
   */
  get selectedOptionsValues() {
    const selectedOptions = [];

    /*
     * Champy's V134: preserve Shopify option order exactly as rendered.
     * This fixes mixed controls such as Color swatches + Tail dropdowns.
     */
    for (const control of this.querySelectorAll('select, fieldset input:checked')) {
      let selected = control;

      if (control instanceof HTMLSelectElement) {
        selected = control.options[control.selectedIndex];
      }

      if (!(selected instanceof HTMLElement)) continue;
      selectedOptions.push(selected);
    }

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) {
        throw new Error('No option value ID found');
      }

      return optionValueId;
    });
  }

}

if (
  !customElements.get(
    'variant-picker'
  )
) {
  customElements.define(
    'variant-picker',
    VariantPicker
  );
}